/**
 * Logger Wrapper - Interface amigável para Datadog Logs
 *
 * Este utilitário encapsula o SDK do Datadog Logs e fornece uma API simplificada
 * para logging estruturado na aplicação.
 *
 * Benefícios:
 * - API consistente e type-safe
 * - Fallback automático para console.log caso Datadog não esteja inicializado
 * - Suporte a contexto adicional em cada log
 * - Integração automática com o trace_id do RUM (rastreabilidade completa)
 *
 * @example
 * ```typescript
 * import Logger from '@/utils/logger';
 *
 * // Log simples
 * Logger.info('User logged in');
 *
 * // Log com contexto adicional
 * Logger.info('Payment processed', { orderId: '123', amount: 99.9 });
 *
 * // Log de erro
 * try {
 *   await processPayment();
 * } catch (error) {
 *   Logger.error('Payment failed', { error, orderId: '123' });
 * }
 * ```
 */

import type { datadogLogs as DatadogLogsType } from '@datadog/browser-logs';
import type { datadogRum as DatadogRumType } from '@datadog/browser-rum';

/** Níveis de log suportados */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Contexto adicional serializável para logs */
export interface LogContext {
  [key: string]: unknown;
}

/**
 * Metadados de erro estruturado
 *
 * Campo especial:
 * - skipRemote: quando true, o log será feito apenas localmente (console),
 *   mesmo que o Datadog esteja disponível. Útil para validações/erros que
 *   só fazem sentido em ambiente local (ex: PostHog não configurado).
 */
export interface ErrorDetails {
  error?: unknown;
  statusCode?: number;
  url?: string;
  method?: string;
  requestId?: string;
  skipRemote?: boolean;
  [key: string]: unknown;
}

let ddRum: typeof DatadogRumType | null = null;
let ddLogs: typeof DatadogLogsType | null = null;
let sdkLoadPromise: Promise<void> | null = null;

function loadSdk(): Promise<void> {
  if (ddRum && ddLogs) {
    return Promise.resolve();
  }
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }
  sdkLoadPromise = Promise.all([import('@datadog/browser-rum'), import('@datadog/browser-logs')]).then(
    ([rum, logs]) => {
      ddRum = rum.datadogRum;
      ddLogs = logs.datadogLogs;
    },
  );
  return sdkLoadPromise;
}

/** Pre-load the SDK (called by datadog.ts after init) */
export function preloadDatadogSdk(): Promise<void> {
  return loadSdk();
}

class Logger {
  private isDatadogAvailable(): boolean {
    if (!ddRum) {
      return false;
    }
    try {
      return ddRum.getInternalContext() !== undefined;
    } catch {
      return false;
    }
  }

  /**
   * Obtém o session_id atual da sessão RUM (para correlação entre ferramentas)
   *
   * Método público para permitir acesso externo ao session_id,
   * útil para integração com outros sistemas de tracking (ex: PostHog).
   */
  public getSessionId(): string | undefined {
    if (!ddRum) {
      return undefined;
    }
    try {
      const rumContext = ddRum.getInternalContext();
      return rumContext?.session_id || undefined;
    } catch {
      return undefined;
    }
  }

  private getServiceName(): string {
    return String(import.meta.env.VITE_DATADOG_SERVICE_MY_MENLO || 'my-menlo');
  }

  private enrichContext(context: LogContext = {}): LogContext {
    const enriched: LogContext = { ...context };
    enriched.service = this.getServiceName();

    const sessionId = this.getSessionId();
    if (sessionId) {
      enriched.session_id = sessionId;
    }

    const serializable: LogContext = {};
    for (const [key, value] of Object.entries(enriched)) {
      if (value === undefined) {
        continue;
      }
      serializable[key] = value;
    }

    return serializable;
  }

  private log(level: LogLevel, message: string, context?: LogContext): void {
    const { skipRemote, ...restContext } = (context || {}) as LogContext & { skipRemote?: boolean };
    const enrichedContext = this.enrichContext(restContext);

    const canUseDatadog = this.isDatadogAvailable() && !skipRemote;

    if (canUseDatadog && ddLogs) {
      ddLogs.logger[level](message, enrichedContext);
    } else {
      const consoleMethod = level === 'debug' ? 'log' : level;
      // eslint-disable-next-line no-console
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, enrichedContext);
    }
  }

  /** Log de nível DEBUG (apenas console) */
  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }

  /** Log de nível INFO (apenas console) */
  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  /** Log de nível WARN (apenas console) */
  warn(message: string, context?: LogContext): void {
    this.log('warn', message, context);
  }

  /** Log de nível ERROR (console + RUM) */
  error(message: string, details?: ErrorDetails): void {
    const context: LogContext = { ...details };
    const originalError = details?.error;
    const isErrorInstance = originalError instanceof Error;
    const skipRemote = details?.skipRemote === true;

    if (isErrorInstance && originalError) {
      context.errorMessage = originalError.message;
      context.errorName = originalError.name;
      delete context.error;
    } else if (originalError) {
      context.errorData = originalError;
      delete context.error;
    }

    const errorToSend = isErrorInstance ? originalError : new Error(message);

    this.log('error', message, context);

    // Também registra no RUM como erro (para aparecer na timeline de sessão),
    // exceto quando explicitamente marcado como apenas local (skipRemote).
    if (this.isDatadogAvailable() && ddRum && !skipRemote) {
      try {
        ddRum.addError(errorToSend, {
          custom_message: message,
          ...this.enrichContext(context),
        });
      } catch (rumError) {
        console.error('[Logger] Erro ao enviar para Datadog RUM:', rumError);
      }
    }
  }

  /** Log especializado para erros de API HTTP */
  apiError(message: string, details: ErrorDetails): void {
    this.error(`[API Error] ${message}`, { ...details, errorType: 'api' });
  }

  /** Registra evento customizado no RUM e loga como INFO */
  trackEvent(eventName: string, context?: LogContext): void {
    if (this.isDatadogAvailable() && ddRum) {
      ddRum.addAction(eventName, context);
    }
    this.info(`[Event] ${eventName}`, context);
  }

  /** Inicia span e retorna função para finalizar com métricas de performance */
  startSpan(operationName: string): (context?: LogContext) => void {
    const startTime = performance.now();

    return (context?: LogContext) => {
      const duration = performance.now() - startTime;
      const durationFormatted = duration.toFixed(2);
      const durationMs = Number(durationFormatted);

      if (this.isDatadogAvailable() && ddRum) {
        ddRum.addAction(`timing_${operationName}`, {
          duration_ms: durationMs,
          status: 'completed',
          ...context,
        });
      }

      this.info(`[Span] ${operationName} finished in ${durationFormatted}ms`, {
        ...context,
        duration: durationMs,
        operation: operationName,
      });
    };
  }
}

/** Instância única do Logger */
const logger = new Logger();

export default logger;

/**
 * Expõe o session_id atual da sessão RUM para outros sistemas de tracking (ex: PostHog).
 */
export function getCurrentSessionId(): string | undefined {
  return logger.getSessionId();
}
